import React, { useCallback, useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

interface HistoriqueIntervention {
  id: number;
  numero_intervention: string | null;
  type_intervention: string;
  statut: 'finished' | 'cancelled' | 'failed';
  adresse: string | null;
  departement: string | null;
  victimes_count: number | null;
  vehicules_count: number | null;
  started_at: string | null;
  finished_at: string | null;
  argent_recompense: number | null;
  xp_recompense: number | null;
}

interface HistoriqueStats {
  total_interventions: number;
  reussies: number;
  annulees: number;
  echouees: number;
  total_argent: number;
  total_xp: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const STATUT_CONFIG: Record<string, { label: string; cls: string }> = {
  finished:  { label: 'Réussie',  cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  cancelled: { label: 'Annulée',  cls: 'bg-zinc-700/50 text-zinc-400 border-zinc-600/30' },
  failed:    { label: 'Échouée',  cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDuration(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms <= 0) return '—';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h${String(min % 60).padStart(2, '0')}`;
}

const HistoriqueView: React.FC = () => {
  const [interventions, setInterventions] = useState<HistoriqueIntervention[]>([]);
  const [stats, setStats] = useState<HistoriqueStats | null>(null);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [statutFilter, setStatutFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async (currentPage: number, statut: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(currentPage), limit: '20' });
      if (statut) params.set('statut', statut);
      const [listRes, statsRes] = await Promise.all([
        fetch(`${getApiBaseUrl(true)}/historique/interventions?${params}`, { credentials: 'include' }),
        fetch(`${getApiBaseUrl(true)}/historique/stats`, { credentials: 'include' }),
      ]);
      const listData = await listRes.json();
      const statsData = await statsRes.json();
      if (listData.success) {
        setInterventions(listData.data.interventions);
        setPagination(listData.data.pagination);
      }
      if (statsData.success) setStats(statsData.data.stats);
    } catch {
      // noop — affichage partiel si réseau KO
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(page, statutFilter); }, [fetchData, page, statutFilter]);

  const handleStatutChange = (s: string) => {
    setStatutFilter(s);
    setPage(1);
  };

  return (
    <div className="h-full flex flex-col gap-4 p-4 overflow-y-auto">

      {/* Statistiques globales */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 shrink-0">
          {[
            { label: 'Total',    value: stats.total_interventions, cls: 'text-sky-400' },
            { label: 'Réussies', value: stats.reussies,            cls: 'text-emerald-400' },
            { label: 'Annulées', value: stats.annulees,            cls: 'text-zinc-400' },
            { label: 'Échouées', value: stats.echouees,            cls: 'text-red-400' },
            { label: 'Argent',   value: `${Number(stats.total_argent).toLocaleString('fr-FR')} €`, cls: 'text-amber-400' },
            { label: 'XP',       value: Number(stats.total_xp).toLocaleString('fr-FR'), cls: 'text-purple-400' },
          ].map(({ label, value, cls }) => (
            <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-center">
              <p className={`text-xl font-bold ${cls}`}>{value}</p>
              <p className="text-[11px] text-zinc-500 mt-0.5 uppercase tracking-wide">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filtres */}
      <div className="flex items-center gap-3 shrink-0">
        <p className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">Filtrer :</p>
        {[
          { value: '',          label: 'Toutes' },
          { value: 'finished',  label: 'Réussies' },
          { value: 'cancelled', label: 'Annulées' },
          { value: 'failed',    label: 'Échouées' },
        ].map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => handleStatutChange(value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
              statutFilter === value
                ? 'bg-sky-500/20 text-sky-300 border-sky-500/40'
                : 'bg-zinc-800/60 text-zinc-400 border-zinc-700/50 hover:text-zinc-200'
            }`}
          >
            {label}
          </button>
        ))}
        <span className="ml-auto text-xs text-zinc-600">{pagination.total} intervention(s)</span>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center text-zinc-600 text-sm">Chargement…</div>
      ) : interventions.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-zinc-600">
          <svg className="w-12 h-12 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-sm">Aucune intervention terminée</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col gap-2">
          {interventions.map((inv) => {
            const s = STATUT_CONFIG[inv.statut] ?? { label: inv.statut, cls: 'bg-zinc-700 text-zinc-300 border-zinc-600' };
            return (
              <div key={inv.id} className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {inv.numero_intervention && (
                      <span className="text-[11px] font-mono text-zinc-500">#{inv.numero_intervention}</span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${s.cls}`}>{s.label}</span>
                    <span className="text-xs text-zinc-300 font-medium capitalize">
                      {inv.type_intervention.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 truncate">
                    {inv.adresse ?? 'Adresse inconnue'}{inv.departement ? ` — Dép. ${inv.departement}` : ''}
                  </p>
                  <p className="text-[11px] text-zinc-600 mt-1">{formatDate(inv.finished_at)}</p>
                </div>
                <div className="flex flex-wrap gap-3 text-xs shrink-0">
                  <span className="text-zinc-400">
                    <span className="text-zinc-600 mr-1">Durée</span>
                    {formatDuration(inv.started_at, inv.finished_at)}
                  </span>
                  {(inv.vehicules_count ?? 0) > 0 && (
                    <span className="text-zinc-400">
                      <span className="text-zinc-600 mr-1">Véhicules</span>
                      {inv.vehicules_count}
                    </span>
                  )}
                  {(inv.victimes_count ?? 0) > 0 && (
                    <span className="text-zinc-400">
                      <span className="text-zinc-600 mr-1">Victimes</span>
                      {inv.victimes_count}
                    </span>
                  )}
                  {(inv.argent_recompense ?? 0) > 0 && (
                    <span className="text-amber-400 font-semibold">
                      +{Number(inv.argent_recompense).toLocaleString('fr-FR')} €
                    </span>
                  )}
                  {(inv.xp_recompense ?? 0) > 0 && (
                    <span className="text-purple-400 font-semibold">
                      +{Number(inv.xp_recompense).toLocaleString('fr-FR')} XP
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 shrink-0 pt-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 text-xs disabled:opacity-30 hover:bg-zinc-700 transition-colors"
          >
            ← Précédent
          </button>
          <span className="text-xs text-zinc-500">
            Page {pagination.page} / {pagination.totalPages}
          </span>
          <button
            type="button"
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 text-xs disabled:opacity-30 hover:bg-zinc-700 transition-colors"
          >
            Suivant →
          </button>
        </div>
      )}
    </div>
  );
};

export default HistoriqueView;
